import { useState } from "react";
import type { FormEvent } from "react";

const FORM_URL = "https://readdy.ai/api/form/daeep9jep053ijub1bfg";

type Status = "idle" | "success" | "error";

export default function AboutContact() {
  const [status, setStatus] = useState<Status>("idle");
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const data = new FormData(form);

    const honeypot = String(data.get("company_alt") ?? "").trim();
    if (honeypot) {
      setStatus("success");
      setMessage("感谢你的线索，编辑部已收到并会尽快核实。");
      form.reset();
      return;
    }

    data.delete("company_alt");
    setLoading(true);
    setStatus("idle");
    setMessage("");
    try {
      const res = await fetch(FORM_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams(data as unknown as Record<string, string>).toString(),
      });
      const text = await res.text();
      let parsed: Record<string, unknown> | null = null;
      try {
        parsed = JSON.parse(text);
      } catch {
        parsed = null;
      }
      const meta = (parsed?.meta ?? {}) as Record<string, unknown>;
      const msg = String(meta?.message ?? parsed?.message ?? "");
      if (res.ok && parsed?.code === "OK") {
        setStatus("success");
        setMessage("感谢你的爆料与信任，编辑部已收到，核实后会第一时间跟进。");
        form.reset();
      } else {
        setStatus("error");
        setMessage(msg || "提交未成功，请稍后重试。");
      }
    } catch {
      setStatus("error");
      setMessage("网络异常，提交未成功，请稍后重试。");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" className="scroll-mt-20 py-12 md:py-16 bg-background-50">
      <div className="mx-auto max-w-7xl px-4 md:px-6">
        <div className="rounded-2xl bg-accent-900 overflow-hidden">
          <div className="grid grid-cols-1 lg:grid-cols-2">
            <div className="p-6 md:p-10 text-accent-100 flex flex-col justify-center">
              <p className="text-xs font-semibold tracking-[0.3em] text-accent-400">GET IN TOUCH</p>
              <h2 className="mt-3 font-heading font-bold text-2xl md:text-3xl text-background-50 leading-snug">
                关于51影院
              </h2>
              <p className="mt-4 text-sm leading-relaxed text-accent-200/90">
                51影院是一家专注娱乐内容与演员资讯的媒体平台。我们聚焦演员情史、剧组内幕、行业薪酬、幕后职业人群与职业素养等话题，坚持采编与商务分离，以多方核实、尊重隐私、克制表达为内容准则。
              </p>
              <p className="mt-4 text-sm leading-relaxed text-accent-200/90">
                我们欢迎读者、剧组从业者与当事人提供线索。若你对某篇内容有异议，或希望提交一份爆料，欢迎通过右侧表单与我们取得联系，我们会在核实后及时回应。
              </p>
              <dl className="mt-6 flex flex-col gap-3 text-sm">
                <div className="flex items-center gap-2.5">
                  <i className="ri-mail-line text-accent-400"></i>
                  <span>hello@51yingyuan.example.com</span>
                </div>
                <div className="flex items-center gap-2.5">
                  <i className="ri-chat-follow-up-line text-accent-400"></i>
                  <span>商务 / 线索 / 纠错，欢迎来信</span>
                </div>
              </dl>
            </div>

            <div className="bg-background-50 p-6 md:p-10 m-0 lg:m-4 rounded-none lg:rounded-xl">
              <h3 className="font-heading font-bold text-xl text-foreground-950">我要爆料 / 提交线索</h3>
              <p className="mt-2 text-sm text-foreground-500">请尽量提供可核实的时间、场合与依据，编辑部会认真对待每一条信息。</p>

              <form
                id="gossip-form"
                data-readdy-form="吃瓜爆料投稿表单"
                onSubmit={onSubmit}
                className="mt-6 flex flex-col gap-4"
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <label className="flex flex-col gap-1.5 text-sm text-foreground-700">
                    你的称呼
                    <input name="name" type="text" placeholder="怎么称呼你" className="h-11 rounded-md border border-background-200 bg-background-50 px-3 text-sm outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-200" />
                  </label>
                  <label className="flex flex-col gap-1.5 text-sm text-foreground-700">
                    联系邮箱
                    <input name="email" type="email" placeholder="用于跟进反馈" required className="h-11 rounded-md border border-background-200 bg-background-50 px-3 text-sm outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-200" />
                  </label>
                </div>
                <label className="flex flex-col gap-1.5 text-sm text-foreground-700">
                  线索主题
                  <select name="topic" className="h-11 rounded-md border border-background-200 bg-background-50 px-3 text-sm outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-200">
                    <option value="现场爆料">现场爆料</option>
                    <option value="行业线索">行业线索</option>
                    <option value="内容纠错">内容纠错</option>
                    <option value="商务合作">商务合作</option>
                    <option value="其他">其他</option>
                  </select>
                </label>
                <label className="flex flex-col gap-1.5 text-sm text-foreground-700">
                  详细内容
                  <textarea name="content" rows={4} maxLength={500} placeholder="请描述事件的时间、场合、经过与可核实依据（500字以内）" className="rounded-md border border-background-200 bg-background-50 px-3 py-2.5 text-sm outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-200 resize-none"></textarea>
                </label>
                <input name="company_alt" type="text" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly className="honeypot" />
                <button
                  type="submit"
                  disabled={loading}
                  className="inline-flex items-center justify-center gap-2 h-12 rounded-md bg-primary-600 text-background-50 text-sm font-semibold hover:bg-primary-700 disabled:opacity-60 whitespace-nowrap"
                >
                  {loading ? <i className="ri-loader-4-line animate-spin text-base"></i> : <i className="ri-send-plane-line text-base"></i>}
                  {loading ? "提交中…" : "提交线索"}
                </button>
                {status === "success" && (
                  <p className="text-sm text-primary-600 flex items-center gap-2">
                    <i className="ri-checkbox-circle-line"></i>{message}
                  </p>
                )}
                {status === "error" && (
                  <p className="text-sm text-primary-600 flex items-center gap-2">
                    <i className="ri-error-warning-line"></i>{message}
                  </p>
                )}
              </form>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}